import { createSvgEl } from '@learnforge/demo-render';

// ── Layout constants ─────────────────────────────────────────────────────────

const W = 400;
const H = 300;
const PAD = 32;
/** Scale: SVG pixels per unit (1 unit = 1 on the math plane). */
const SCALE = 40;

/** Math origin → SVG coords. */
const OX = W / 2;
const OY = H / 2;

/** Convert a math coordinate to an SVG x coordinate. */
function toSvgX(re: number): number {
  return OX + re * SCALE;
}

/** Convert a math coordinate to an SVG y coordinate (y-axis inverted). */
function toSvgY(im: number): number {
  return OY - im * SCALE;
}

// ── Public API ───────────────────────────────────────────────────────────────

export interface ComplexPlaneOptions {
  point?: { re: number; im: number } | null;
}

export interface ComplexPlaneHandle {
  group: SVGGElement;
  update(opts: Partial<ComplexPlaneOptions>): void;
  destroy(): void;
}

/**
 * Renders a complex-plane diagram as an SVG group.
 *
 * - Real axis (horizontal) and imaginary axis (vertical) each carry a
 *   `data-axis` attribute (`"real"` and `"im"`).
 * - When `point` is set, one `<circle data-point>` is placed at the
 *   corresponding SVG coordinates (math y-axis is inverted: +im is up).
 * - A purely real number (im = 0) sits exactly on the real axis.
 * - No inline colours.
 */
export function createComplexPlane(
  parent: SVGSVGElement | SVGGElement,
  opts: ComplexPlaneOptions,
): ComplexPlaneHandle {
  let current: Required<ComplexPlaneOptions> = { point: opts.point ?? null };

  const group = createSvgEl('g', { class: 'cp-plane' });
  parent.appendChild(group);

  // ── Axes ──

  // Real axis (horizontal)
  const realAxis = createSvgEl('line', {
    class: 'cp-axis cp-axis--real',
    'data-axis': 'real',
    x1: PAD,
    y1: OY,
    x2: W - PAD,
    y2: OY,
  });
  group.appendChild(realAxis);

  // Imaginary axis (vertical)
  const imAxis = createSvgEl('line', {
    class: 'cp-axis cp-axis--im',
    'data-axis': 'im',
    x1: OX,
    y1: PAD,
    x2: OX,
    y2: H - PAD,
  });
  group.appendChild(imAxis);

  // Axis labels
  const realLabel = createSvgEl('text', {
    class: 'cp-axis-label',
    x: W - PAD - 4,
    y: OY - 6,
    'text-anchor': 'end',
  });
  realLabel.textContent = 'ℝ';
  group.appendChild(realLabel);

  const imLabel = createSvgEl('text', {
    class: 'cp-axis-label',
    x: OX + 6,
    y: PAD + 4,
    'text-anchor': 'start',
    'dominant-baseline': 'hanging',
  });
  imLabel.textContent = 'ℑ';
  group.appendChild(imLabel);

  // ── Point marker (created on demand) ──
  let pointMarker: SVGCircleElement | null = null;

  function renderPoint(): void {
    if (current.point != null) {
      const svgX = toSvgX(current.point.re);
      const svgY = toSvgY(current.point.im);
      if (pointMarker === null) {
        pointMarker = createSvgEl('circle', {
          class: 'cp-point',
          'data-point': '',
          r: 5,
        });
        group.appendChild(pointMarker);
      }
      pointMarker.setAttribute('cx', String(svgX));
      pointMarker.setAttribute('cy', String(svgY));
    } else {
      pointMarker?.remove();
      pointMarker = null;
    }
  }

  renderPoint();

  return {
    group,

    update(partial: Partial<ComplexPlaneOptions>): void {
      if ('point' in partial) {
        current = { ...current, point: partial.point ?? null };
      }
      renderPoint();
    },

    destroy(): void {
      group.remove();
    },
  };
}
